import type { ComponentProps } from 'react'
import { cn } from '../lib/cn'
import logoColour from '../assets/logo-colour.png'
import logoWhite from '../assets/logo-white.png'

/**
 * Brand marks and the third-party identity chrome that goes with them.
 *
 * The wordmark is a raster because it is one in Figma too (`final logo
 * transparent 2`), and a hand-traced SVG would be a different mark, not the
 * same one at higher fidelity. It is 343x113 at 2x — the frames place it at
 * 176x58, so it is served at exactly the density it was drawn for.
 */

export type LogoProps = Omit<ComponentProps<'img'>, 'src' | 'alt'> & {
  /** `white` is the knockout used on the splash and welcome gradients. */
  tone?: 'colour' | 'white'
}

export function Logo({ tone = 'colour', className, ...props }: LogoProps) {
  return (
    <img
      src={tone === 'white' ? logoWhite : logoColour}
      // The tagline is part of the artwork, so it belongs in the alt text.
      alt="WaaGo — Move. Deliver. Connect"
      width={176}
      height={58}
      className={cn('h-[58px] w-[176px] object-contain', className)}
      {...props}
    />
  )
}

/**
 * Benin's flag, as it appears in the phone field (32x24 in the frames).
 *
 * Drawn rather than imported: three rectangles is less bytes than any flag
 * sprite, and it stays crisp at every density.
 */
export function BeninFlag({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg
      viewBox="0 0 32 24"
      role="img"
      aria-label="Bénin"
      className={cn('h-6 w-8 shrink-0', className)}
      {...props}
    >
      <rect width="32" height="24" fill="#fcd116" />
      <rect y="12" width="32" height="12" fill="#e8112d" />
      <rect width="12" height="24" fill="#008751" />
    </svg>
  )
}

/** Brand glyphs, traced from the vectors in the Figma `Social icon` component. */
export function FacebookIcon({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={cn('size-6', className)} {...props}>
      <circle cx="12" cy="12" r="12" fill="#1877f2" />
      <path
        fill="#fff"
        d="M16.5 15.47l.53-3.47h-3.33V9.75c0-.95.47-1.88 1.96-1.88h1.51V4.92s-1.37-.23-2.68-.23c-2.74 0-4.53 1.66-4.53 4.66V12H6.9v3.47h3.06V24h3.76v-8.53h2.79z"
      />
    </svg>
  )
}

export function GoogleIcon({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={cn('size-6', className)} {...props}>
      <path
        fill="#4285f4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.46a5.52 5.52 0 01-2.4 3.62v3.01h3.88c2.27-2.09 3.58-5.17 3.58-8.82z"
      />
      <path
        fill="#34a853"
        d="M12 24c3.24 0 5.96-1.08 7.94-2.91l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.95H1.26v3.11A12 12 0 0012 24z"
      />
      <path
        fill="#fbbc04"
        d="M5.27 14.28a7.2 7.2 0 010-4.56V6.61H1.26a12 12 0 000 10.78l4.01-3.11z"
      />
      <path
        fill="#ea4335"
        d="M12 4.77c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 001.26 6.61l4.01 3.11C6.22 6.88 8.87 4.77 12 4.77z"
      />
    </svg>
  )
}

export function AppleIcon({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={cn('size-6', className)} {...props}>
      <path
        fill="#000"
        d="M17.05 12.62c-.03-2.7 2.2-4 2.3-4.06-1.25-1.83-3.2-2.08-3.89-2.11-1.66-.17-3.24.97-4.08.97-.84 0-2.14-.95-3.51-.92-1.81.03-3.48 1.05-4.4 2.66-1.88 3.26-.48 8.08 1.35 10.72.9 1.29 1.96 2.74 3.36 2.69 1.35-.06 1.86-.87 3.49-.87 1.63 0 2.09.87 3.51.84 1.45-.03 2.37-1.32 3.26-2.62 1.03-1.5 1.45-2.95 1.47-3.03-.03-.01-2.83-1.09-2.86-4.27zM14.4 4.62c.74-.9 1.24-2.15 1.1-3.4-1.07.05-2.36.72-3.13 1.61-.69.79-1.29 2.06-1.13 3.28 1.19.09 2.41-.61 3.16-1.49z"
      />
    </svg>
  )
}

const PROVIDERS = {
  facebook: { label: 'Facebook', Icon: FacebookIcon },
  google: { label: 'Google', Icon: GoogleIcon },
  apple: { label: 'Apple', Icon: AppleIcon },
} as const

export type SocialProvider = keyof typeof PROVIDERS

export type SocialButtonProps = Omit<ComponentProps<'button'>, 'children'> & {
  provider: SocialProvider
  /** `'Se connecter avec'` on login, `'S’inscrire avec'` on registration. */
  action: string
}

/**
 * A federated sign-in button: 44px tall, white on a slate-200 hairline, with
 * the glyph and label centred together as a unit rather than the glyph pinned
 * to the left edge.
 *
 * Never render it disabled for "not built yet". A greyed-out row is a different
 * screen from the one that was designed, and it tells the user nothing; a
 * button that answers when pressed keeps the frame intact and is honest about
 * where the feature stands.
 */
export function SocialButton({ provider, action, className, ...props }: SocialButtonProps) {
  const { label, Icon } = PROVIDERS[provider]
  return (
    <button
      type="button"
      className={cn(
        'flex h-11 w-full items-center justify-center gap-2.5 rounded-md border',
        'border-slate-200 bg-white text-base font-medium text-slate-600',
        'transition-colors hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <Icon />
      {action} {label}
    </button>
  )
}

/** The `ou` rule between the primary action and the federated options. */
export function OrDivider({ label = 'ou', className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="h-px flex-1 bg-slate-400" />
      <span className="text-xs text-slate-500">{label}</span>
      <span className="h-px flex-1 bg-slate-400" />
    </div>
  )
}
